<script>
import {
  required,
  requiredIf,
  integer,
  minValue,
  maxValue,
  helpers
} from 'vuelidate/lib/validators'

import CustomSeatSpecs from '@/router/views/sales/my-orders/view/custom-seat/specs.vue'
import { isFoamFilenameMismatch, detectFoamTypesFromFilename } from './foam-filename-codes'

const notSame = field => (val, vm) => val !== vm[field]

import { authComputed } from '@/state/helpers'

export default {
  name: 'cad-info',
  props: {
    value: { type: Object, default: () => { } }
  },
  components: { CustomSeatSpecs },
  data: vm => ({
    form: {
      file_name: null,
      specs: {
        foam_type: null,
        mix_seat_foam_type: null,
        mix_back_foam_type: null,
        offset_3d: 0,
        widen: 0,
        lengthen: 0,
        inclination: 'legs_horizontal',
        inclination_custom: 0,
        symmetrization: null,
        can_use_glue: false,
        mattress_abduction_type: null
      },
      ...vm.value
    }
  }),
  validations() {
    let validations = {
      form: {
        specs: {
          foam_type: {
            required
          },
          inclination_custom: {
            required: requiredIf(model => model.inclination === 'custom'),
            integer,
            minValue: minValue(0),
            maxValue: maxValue(10)
          },
          widen: {
            required,
            integer
          },
          lengthen: {
            required,
            integer
          },
          offset_3d: {
            required,
            integer,
            minValue: minValue(0),
            maxValue: maxValue(20)
          },
          mattress_abduction_type: {
            required: requiredIf(
              model =>
                model.model_type === 'full_mattress' ||
                model.model_type === 'half_mattress'
            )
          }
        }
      }
    }

    if (this.form.specs.foam_type === 'mix') {
      validations.form.specs.mix_seat_foam_type = {
        required,
        notSame: notSame('mix_back_foam_type')
      }
      validations.form.specs.mix_back_foam_type = {
        required,
        notSame: notSame('mix_seat_foam_type')
      }
    }

    return validations
  },
  methods: {
    validate() {
      if (!this.$v && !this.$v.form) return true
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.$emit('validated', { ...this.form })
        this.$emit('input', { ...this.form })
      }
      return !this.$v.form.$invalid
    }
  },
  computed: {
    ...authComputed,
    ALLOWED_MATTRESS_ABDUCTION_TYPES() {
      return [
        { value: null, text: this.$t('attribute.mattress_abd_type.select') },
        { value: 'high', text: this.$t('attribute.mattress_abd_type.high') },
        { value: 'model', text: this.$t('attribute.mattress_abd_type.model') }
      ]
    },
    ALLOWED_MIX_FOAM_TYPES() {
      var foams = [
        { value: null, text: ' - Select a Foam Quality -' },
        { value: 'f32', text: this.$t('attribute.foam_type.f32') },
        { value: 'rg50', text: this.$t('attribute.foam_type.rg50') },
        { value: 'rg70', text: this.$t('attribute.foam_type.rg70') },
        { value: 's4044', text: this.$t('attribute.foam_type.s4044') },
        { value: 'ld24', text: this.$t('attribute.foam_type.ld24') }
      ]

      return foams
    },
    FOAM_TYPES() {
      var allowMixFoam = this.userProfile.preferences.allow_mixed_foams

      var foams = [
        { value: null, text: ' - Select a Foam Quality -' },
        { value: 'f32', text: this.$t('attribute.foam_type.f32') },
        { value: 'rg50', text: this.$t('attribute.foam_type.rg50') },
        { value: 'rg70', text: this.$t('attribute.foam_type.rg70') },
        { value: 's4044', text: this.$t('attribute.foam_type.s4044') },
        { value: 'ld24', text: this.$t('attribute.foam_type.ld24') }
      ]

      if (allowMixFoam && this.form.specs.model_type === 'full_seat') {
        foams = [
          ...foams,
          { value: 'mix', text: this.$t('attribute.foam_type.mix') }
        ]
      }

      return foams
    },

    SYMMETRIZATION() {
      return [
        {
          value: null,
          text: 'No Symmetrization'
        },
        { value: 'left', text: this.$t('attribute.symmetrization.left') },
        { value: 'right', text: this.$t('attribute.symmetrization.right') }
      ]
    },

    INCLINATION() {
      return [
        {
          value: 'legs_horizontal',
          text: this.$t('attribute.inclination.legs_horizontal')
        },
        {
          value: 'back_straight',
          text: this.$t('attribute.inclination.back_straight')
        },
        { value: 'custom', text: this.$t('attribute.inclination.custom') }
      ]
    },

    ALLOW_LENGHTEN() {
      return ['full_mattress', 'half_mattress'].includes(
        this.form.specs.model_type
      )
    },

    foamFilenameMismatch() {
      return isFoamFilenameMismatch(
        this.form.file_name,
        this.form.specs.foam_type
      )
    },

    detectedFoamTypesFromFilename() {
      return detectFoamTypesFromFilename(this.form.file_name)
    },

    foamFilenameMismatchMessage() {
      const labels = this.detectedFoamTypesFromFilename.map(type =>
        this.$t(`attribute.foam_type_short.${type}`)
      )
      return this.$t('validations.foamFilenameMismatch', {
        foamType: labels.join(', ')
      })
    }
  }
}
</script>
<template>
  <b-row>
    <b-col cols="12">
      <g-form-error-wrapper :validations="$v.form.specs.foam_type">
        <template v-slot="{ errors, invalid }">
          <div>
            <g-select v-model.number="form.specs.foam_type" :label="$t('Foam Quality')" :options="FOAM_TYPES"
              :error-messages="errors" :is-valid="!invalid" v-on:change="$v.form.specs.foam_type.$touch()"
              label-cols-sm="6" label-cols-lg="4" max-width="280px">
            </g-select>
            <div v-if="foamFilenameMismatch" class="d-flex align-items-center text-danger uppercase my-3">
              <i class="bx bx-error mr-3 font-size-24"></i>
              <div>
                <div>{{ foamFilenameMismatchMessage }}</div>
                <div class="normal-case mt-1 font-italic">
                  Selected File: {{ form.file_name }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </g-form-error-wrapper>

      <template v-if="form.specs.foam_type == 'mix'">
        <g-form-error-wrapper :validations="$v.form.specs.mix_seat_foam_type">
          <template v-slot="{ errors, invalid }">
            <g-select v-model.number="form.specs.mix_seat_foam_type" :label="$t('Seat Foam Quality')"
              :options="ALLOWED_MIX_FOAM_TYPES" :error-messages="errors" :is-valid="!invalid"
              v-on:change="$v.form.specs.mix_seat_foam_type.$touch()" label-cols-sm="6" label-cols-lg="4"
              max-width="280px">
            </g-select>
          </template>
        </g-form-error-wrapper>
        <g-form-error-wrapper :validations="$v.form.specs.mix_back_foam_type">
          <template v-slot="{ errors, invalid }">
            <g-select v-model.number="form.specs.mix_back_foam_type" :label="$t('Back Foam Quality')"
              :options="ALLOWED_MIX_FOAM_TYPES" :error-messages="errors" :is-valid="!invalid"
              v-on:change="$v.form.specs.mix_back_foam_type.$touch()" label-cols-sm="6" label-cols-lg="4"
              max-width="280px">
            </g-select>
          </template>
        </g-form-error-wrapper>
      </template>

      <template v-if="form.specs.is_remill && !form.not_found">
        <h6 class="mt-4 mb-3">{{ $t('Previous Specifications') }}</h6>
        <custom-seat-specs :specs="form.specs" :show-last-foam-type="true"></custom-seat-specs>
      </template>

      <template v-if="!form.specs.is_remill">
        <g-form-error-wrapper :validations="$v.form.specs.offset_3d">
          <template v-slot="{ errors, invalid }">
            <g-text-input v-model.number="form.specs.offset_3d" :label="$t('3D Offset')" append="mm" maxWidth="100px"
              text-align="center" :error-messages="errors" :is-valid="!invalid"
              v-on:blur="$v.form.specs.offset_3d.$touch()" label-cols-sm="6" label-cols-lg="4">
            </g-text-input>
          </template>
        </g-form-error-wrapper>
        <g-form-error-wrapper :validations="$v.form.specs.widen">
          <template v-slot="{ errors, invalid }">
            <g-text-input v-model.number="form.specs.widen" :label="$t('Widen Model')" append="mm" maxWidth="100px"
              text-align="center" :error-messages="errors" :is-valid="!invalid" v-on:blur="$v.form.specs.widen.$touch()"
              label-cols-sm="6" label-cols-lg="4">
            </g-text-input>
          </template>
        </g-form-error-wrapper>
        <g-form-error-wrapper :validations="$v.form.specs.lengthen" v-if="ALLOW_LENGHTEN">
          <template v-slot="{ errors, invalid }">
            <g-text-input v-model.number="form.specs.lengthen" :label="$t('Lengthen Model')" append="mm"
              maxWidth="100px" text-align="center" :error-messages="errors" :is-valid="!invalid"
              v-on:blur="$v.form.specs.lengthen.$touch()" label-cols-sm="6" label-cols-lg="4">
            </g-text-input>
          </template>
        </g-form-error-wrapper>

        <g-select v-model="form.specs.symmetrization" :label="$t('Symmetrization')" :options="SYMMETRIZATION"
          label-cols-sm="6" label-cols-lg="4" max-width="200px" />

        <g-select v-model="form.specs.inclination" :label="$t('Inclination')" :options="INCLINATION" label-cols-sm="6"
          label-cols-lg="4" max-width="200px" />

        <g-form-error-wrapper :validations="$v.form.specs.mattress_abduction_type" v-if="
          form.specs.model_type === 'full_mattress' ||
          form.specs.model_type === 'half_mattress'
        ">
          <template v-slot="{ errors, invalid }">
            <g-select v-model.number="form.specs.mattress_abduction_type" :label="$t('Abduction Type')"
              :options="ALLOWED_MATTRESS_ABDUCTION_TYPES" :error-messages="errors" :is-valid="!invalid"
              v-on:change="$v.form.specs.mattress_abduction_type.$touch()" label-cols-sm="6" label-cols-lg="4"
              max-width="250px">
            </g-select>
          </template>
        </g-form-error-wrapper>

        <g-form-error-wrapper :validations="$v.form.specs.inclination_custom"
          v-if="form.specs.inclination === 'custom'">
          <template v-slot="{ errors, invalid }">
            <g-text-input v-model.number="form.specs.inclination_custom" :label="$t('Custom Inclination')" append="cm"
              maxWidth="100px" text-align="center" :error-messages="errors" :is-valid="!invalid"
              v-on:blur="$v.form.specs.inclination_custom.$touch()" label-cols-sm="6" label-cols-lg="4">
            </g-text-input>
          </template>
        </g-form-error-wrapper>
      </template>

      <b-form-checkbox id="can_use_glue" class="my-4" v-model="form.specs.can_use_glue"
        v-if="userProfile.preferences.allow_glueing_enabled" name="can_use_glue">
        {{ $t('Allow use of glued block') }}
      </b-form-checkbox>

      <g-pretty-print :value="$v" v-if="false" />
    </b-col>
  </b-row>
</template>
